import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseItemComponent } from '../../common-items/BaseItem/base-item-form.component';
import { BaseItemProperties, DEFAULT_BASE_PROPERTIES } from '../../common-items/BaseItem/base-item-properties';

import { getPropsFrom } from '@lfw/roesberg.liveforms.core';
import { MatIcon } from '@angular/material/icon';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { DEFAULT_INTERACTIVE_PROPERTIES } from '../../common-items/InteractiveItem/interactive-item-properties';
import { extractColorProperties, getColors, getTextColorsPropertiesStyle } from '../../../../utils/colors';

interface ExternalLinkProperties {
  text: string;
  style: string;
  webDokDefinitionID: string;
  webDokDefinitionName: string;
  webDokMode: string;
  searchTerm: string;
  linkTarget: string;
  docPage: string;
  url: string;
}

@Component({
  selector: 'lfe-external-link-item-form',
  imports: [CommonModule, MatIcon],
  templateUrl: './external-link-item-form.component.html',
  standalone: true,
  styleUrl: './external-link-item-form.component.css'
})
export class ExternalLinkItemFormComponent extends BaseItemComponent<ExternalLinkProperties> {
  readonly translateService = inject(LiveFormsTranslateLoaderService);

  properties: BaseItemProperties & ExternalLinkProperties = {
    ...DEFAULT_BASE_PROPERTIES,
    text: '',
    style: '',
    webDokDefinitionID: '',
    webDokDefinitionName: '',
    webDokMode: '',
    searchTerm: '',
    linkTarget: '',
    docPage: '',
    url: ''
  };

  currentId = computed(() => this.properties.customId || this.properties.id);

  htmlId = computed(() => `external_link_${this.currentId()}`);

  currentTextColor = signal('');
  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));

  href = computed(() => {
    if (!this.properties.url) {
      return '';
    }

    try {
      const fragmentUrl = new URL(this.properties.url);

      if (this.properties.searchTerm) {
        fragmentUrl.searchParams.set('q', this.properties.searchTerm);
      }

      if (this.properties.linkTarget) {
        fragmentUrl.hash = this.properties.linkTarget;
      }

      return fragmentUrl.toString();
    } catch (error) {
      console.warn('Invalid URL:', this.properties.url, error);
      return this.properties.url;
    }
  });

  extractLocalProperties(xml: Element) {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const webDokDefinitionID = props['WebDokDefinitionID'] || '';
    const webDokDefinitionName = props['WebDokDefinitionName'] || '';
    const webDokMode = props['WebDokMode'] || '';
    const searchTerm = props['SearchTerm'] || '';
    const linkTarget = props['LinkTarget'] || '';
    const docPage = props['DocPage'] || '';
    const url = props['Url'] || '';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));
    this.currentTextColor.set(textColor);

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      text,
      style,
      webDokDefinitionID,
      webDokDefinitionName,
      webDokMode,
      searchTerm,
      linkTarget,
      docPage,
      url,
      textBgColor,
      isGenericRepeatItem
    } as BaseItemProperties & ExternalLinkProperties;
  }
}
